"use client";

import { Icon } from "@/components/ui/Icon";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  whatsappLink,
  instagramLink,
  xLink,
  pinterestLink,
  youtubeLink,
} from "@/config/infos";
import { COMPANY } from "@/config/company";
import logo from "@/assets/images/logo/logo-original-ombres-fond-blanc.png";
import logo2 from "@/assets/images/logo/forme logo blanc - PNG fond transparent.png";
import { localeFromPath, stripLocale } from "@/config/i18n";
import { localeNavHref } from "@/config/localeRoutes";
import { FOOTER_NAV, type NavLink } from "@/components/layout/navDict";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";

export function Footer(): JSX.Element {
  const pathname = usePathname();
  const currentPath = pathname ?? "";
  const locale = localeFromPath(currentPath);
  // On raisonne sur le chemin FR canonique (sans préfixe /en ou /ar) pour que
  // les pages location gardent le même habillage quelle que soit la locale.
  const isLocationRoute = stripLocale(currentPath).startsWith("/location");
  const selectedLogo = isLocationRoute ? logo2 : logo;

  const renderLinks = (links: NavLink[]) =>
    links.map((l) => (
      <li key={l.href}>
        <Link
          href={localeNavHref(l.href, locale)}
          className="transition-colors hover:text-primary"
        >
          {l.label[locale]}
        </Link>
      </li>
    ));

  return (
    <footer className="bg-black text-white">
      {/* pb dimensionné pour que le bouton WhatsApp flottant (fixe, bas de page)
          ne recouvre JAMAIS le lien outil discret (bon de transport) tout en bas.
          Mobile : FAB à bottom-20 (80px) + hauteur 64px ⇒ sommet ~144px du bas ;
          on dégage ~160px pour laisser respirer le lien sous le crédit.
          Desktop : FAB à bottom-10 et plus d'espace, 96px suffisent. */}
      <div className="mx-auto max-w-[90rem] px-6 pb-[160px] pt-14 md:px-8 md:pb-[96px] md:pt-14">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Colonne marque + intro + CTA */}
          <div className="lg:col-span-5">
            <div className="mb-4 flex items-center gap-2">
              {/* `sizes` : même raison que dans le Header — logo importé en
                  1656 px, rendu en 32 px. Sans lui, next/image sert la variante
                  3840 px. */}
              <Image
                src={selectedLogo}
                alt="KS Rent Services"
                width={(selectedLogo as any).width}
                height={(selectedLogo as any).height}
                sizes="41px"
                className="h-8 w-auto"
                priority={false}
              />
            </div>
            <p className="mb-5 max-w-md text-sm text-white/85">
              {FOOTER_NAV.intro[locale]}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                href={localeNavHref("/location/vehicules", locale)}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  isLocationRoute
                    ? "bg-white text-primaryLoc hover:bg-gold-200 hover:text-primaryLoc"
                    : "bg-primary text-black hover:bg-gold-600 hover:text-white"
                }`}
              >
                {FOOTER_NAV.cta.rent[locale]}
                <Icon icon="ph:car" className="h-4 w-4" />
              </Link>
              <Link
                href={localeNavHref("/reservation", locale)}
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                  isLocationRoute
                    ? "border-gold-200/50 text-gold-200 hover:border-gold-200 hover:bg-gold-200/10"
                    : "border-white/15 text-white/90 hover:border-primary hover:text-primary"
                }`}
              >
                {FOOTER_NAV.cta.book[locale]}
                <Icon icon="mdi:calendar" className="h-4 w-4" />
              </Link>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                  isLocationRoute
                    ? "border-white/20 text-white/90 hover:border-gold-200 hover:text-gold-200"
                    : "border-white/15 text-white/90 hover:border-primary"
                }`}
              >
                WhatsApp
                <Icon icon="mdi:whatsapp" className="h-4 w-4" />
              </a>
            </div>
            {/* Contact direct — téléphone (cliquable) + email. NAP visible. */}
            <div className="mt-6 flex flex-col gap-2 text-sm">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="inline-flex items-center gap-2 font-semibold text-white transition-colors hover:text-primary"
              >
                <Icon icon="ph:phone-fill" className="h-4 w-4 text-primary" />
                {COMPANY.phoneDisplay}
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="inline-flex items-center gap-2 text-white/85 transition-colors hover:text-primary"
              >
                <Icon
                  icon="ph:envelope-simple"
                  className="h-4 w-4 text-primary"
                />
                {COMPANY.email}
              </a>
            </div>
          </div>

          {/* Liens */}
          <div className="lg:col-span-7">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/90">
                  {FOOTER_NAV.columns.chauffeur[locale]}
                </h3>
                <ul className="space-y-2 text-sm text-white/85">
                  {renderLinks(FOOTER_NAV.chauffeurLinks)}
                </ul>
              </div>
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/90">
                  {FOOTER_NAV.columns.partners[locale]}
                </h3>
                <ul className="space-y-2 text-sm text-white/85">
                  {renderLinks(FOOTER_NAV.partnersLinks)}
                </ul>
              </div>
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/90">
                  {FOOTER_NAV.columns.location[locale]}
                </h3>
                <ul className="space-y-2 text-sm text-white/85">
                  {renderLinks(FOOTER_NAV.locationLinks)}
                </ul>
              </div>
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/90">
                  {FOOTER_NAV.columns.infos[locale]}
                </h3>
                <ul className="space-y-2 text-sm text-white/85">
                  {renderLinks(FOOTER_NAV.infosLinks)}
                </ul>
              </div>
            </div>

            <div className="mt-10 grid gap-8">
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/90">
                  {FOOTER_NAV.columns.legal[locale]}
                </h3>
                <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/85">
                  {renderLinks(FOOTER_NAV.legalLinks)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
          <div className="flex items-center gap-3 text-white/80">
            <a
              href={instagramLink}
              aria-label="Instagram"
              className="transition-colors hover:text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon="mdi:instagram" className="h-5 w-5" />
            </a>
            <a
              href={xLink}
              aria-label="X (Twitter)"
              className="transition-colors hover:text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon="mdi:twitter" className="h-5 w-5" />
            </a>
            <a
              href={pinterestLink}
              aria-label="Pinterest"
              className="transition-colors hover:text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon="mdi:pinterest" className="h-5 w-5" />
            </a>
            <a
              href={youtubeLink}
              aria-label="YouTube"
              className="transition-colors hover:text-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon="mdi:youtube" className="h-5 w-5" />
            </a>
            <span className="ms-1 h-5 w-px bg-white/15" />
            <LanguageSwitcher variant="footer" />
          </div>
          <p className="px-12 text-center text-xs leading-5 text-white/70 sm:px-0">
            &copy; {new Date().getFullYear()} KS Rent Services.{" "}
            {FOOTER_NAV.copyright[locale]}{" "}
            <a
              href="https://www.next-generation.dev/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-white/30 underline-offset-2 hover:text-primary"
            >
              Next Generation
            </a>
            .
          </p>
        </div>

        {/* Outil pro (bon de transport) — volontairement DISCRET et tout en bas :
            destiné aux chauffeurs/partenaires, pas aux clients. */}
        <div className="mt-6 text-center">
          <Link
            href={localeNavHref("/bon-de-transport", locale)}
            className="inline-flex items-center gap-1.5 text-[11px] text-white/40 transition-colors hover:text-white/70"
          >
            <Icon
              icon="mdi:file-document-edit-outline"
              className="h-3.5 w-3.5"
            />
            {FOOTER_NAV.tool.label[locale]}
          </Link>
        </div>
      </div>
    </footer>
  );
}
